import { ref, onMounted, onUnmounted, type Ref } from 'vue';
import { createRafGate } from '../helpers';

type UseScrollBoundsOptions = {
    /** Sub-pixel tolerance for edge and overflow detection. Defaults to 1. */
    edgeTolerance?: number;
};

/**
 * Tracks horizontal scroll position and maximum scrollable distance for a scroll container.
 *
 * @param scrollContainerEl - Ref to the scrollable container element.
 * @param contentEl - Ref to the content element observed for size changes.
 * @param options - Optional configuration for edge tolerance.
 * @returns Reactive scroll bounds, overflow flag, and a scroll event handler.
 */
export const useScrollBounds = (
    scrollContainerEl: Ref<HTMLElement | null>,
    contentEl: Ref<HTMLElement | null>,
    options: UseScrollBoundsOptions = {},
) => {
    const { edgeTolerance = 1 } = options;

    const scrollLeft = ref<number>(0);
    const maxScroll = ref<number>(0);
    const isScrollable = ref<boolean>(false);

    const recomputeBounds = () => {
        if (!scrollContainerEl.value) return;

        const { scrollLeft: left, scrollWidth, clientWidth } = scrollContainerEl.value;
        const overflow = scrollWidth - clientWidth;

        scrollLeft.value = left;
        maxScroll.value = overflow;
        isScrollable.value = overflow > edgeTolerance;
    };

    const { trigger: onScroll, cancel: cancelScrollRaf } = createRafGate(() => {
        if (scrollContainerEl.value) scrollLeft.value = scrollContainerEl.value.scrollLeft;
    });

    const { trigger: onResize, cancel: cancelResizeRaf } = createRafGate(recomputeBounds);

    let resizeObserver: ResizeObserver | null = null;

    onMounted(() => {
        recomputeBounds();

        // rAF-гейт защищает от синхронного ResizeObserver loop
        resizeObserver = new ResizeObserver(onResize);

        // наблюдаем и контейнер и контент
        if (scrollContainerEl.value) resizeObserver.observe(scrollContainerEl.value);
        if (contentEl.value) resizeObserver.observe(contentEl.value);
    });

    onUnmounted(() => {
        cancelScrollRaf();
        cancelResizeRaf();

        resizeObserver?.disconnect();
        resizeObserver = null;
    });

    return { scrollLeft, maxScroll, isScrollable, onScroll };
};
